import {
  FC, useState, useCallback, useMemo,
} from 'react';
import { useTranslation } from 'next-i18next';
import { useForm, useFormState } from 'react-final-form';
import { addMonths } from 'date-fns';
import newTZDate, { formatDate, todayDate } from 'src/common/utils/dateHelpers';
import { useMediaQueries } from 'src/common/providers/MediaQueryProvider';
import { MAXIMUM_MONTHS } from 'src/admin/common/components/SnoozeModal/constants';
import SnoozeBody from 'src/admin/common/components/SnoozeModal/SnoozeBody';
import Datepicker from 'src/common/components/Datepicker';
import SnoozeFooter from 'src/admin/common/components/SnoozeModal/SnoozeFooter';
import { SnoozeFormState } from 'src/admin/common/components/SnoozeModal/types';
import useWizardFlow from 'src/common/components/WizardFlow/useWizardFlow';
import Button from 'src/common/components/ButtonDS22';
import WizardNavigation from 'src/admin/common/components/SnoozeModal/WizardNavigation';
import useDateFnsLocale from 'src/common/hooks/useDateFnsLocale';
import getTitle from './getTitleHelper';
import styles from './CalendarSection.module.scss';

type Props = {};

const CalendarSection: FC<Props> = () => {
  const locale = useDateFnsLocale();
  const [t] = useTranslation('admin-listings');
  const { change } = useForm();
  const { values } = useFormState<SnoozeFormState>();
  const { m: isMediumScreen } = useMediaQueries();
  const today = todayDate();
  const { snooze_start_date: startDate, snooze_end_date: endDate } = values;

  const [selectedRange, setSelectedRange] = useState({
    start: startDate && newTZDate(startDate),
    end: endDate && newTZDate(endDate),
  });

  const { back } = useWizardFlow();

  const onChange = useCallback((dates: Date[]) => {
    const [start, end] = dates;
    setSelectedRange({ start, end });
  }, []);

  const handleSelectDates = useCallback(() => {
    const { start, end } = selectedRange;
    change('snooze_start_date', formatDate(start as Date));
    change('snooze_end_date', formatDate((end ?? start) as Date));

    setSelectedRange({ start, end: end ?? start });
    back();
  }, [back, change, selectedRange]);

  const title = useMemo(() => (
    getTitle(t, selectedRange, locale)
  ), [t, selectedRange, locale]);

  const selectDatesDisabled = !selectedRange.start;

  return (
    <>
      <WizardNavigation>
        {title}
      </WizardNavigation>
      <SnoozeBody>
        <Datepicker
          id="snooze-date-picker"
          minDate={today}
          maxDate={addMonths(today, MAXIMUM_MONTHS)}
          monthsShown={!isMediumScreen && MAXIMUM_MONTHS + 1}
          value={selectedRange.start}
          onChange={onChange}
          startDate={selectedRange.start}
          endDate={selectedRange.end}
          scrollable={!isMediumScreen}
          openToDate={isMediumScreen ? selectedRange.start : today}
          selectsRange
          inline
        />
      </SnoozeBody>

      <SnoozeFooter>
        { isMediumScreen && (
          <button
            type="button"
            className={styles.selectDatesButton}
            onClick={handleSelectDates}
            disabled={selectDatesDisabled}
          >
            {t('snooze-listing--calendar-select-dates')}
          </button>
        )}
        { !isMediumScreen && (
          <Button onClick={handleSelectDates} disabled={selectDatesDisabled}>
            {t('snooze-listing--calendar-select-dates')}
          </Button>
        )}
      </SnoozeFooter>
    </>
  );
};

export default CalendarSection;
